import React from "react";
import { useSelector } from "react-redux";
import { Item } from "./NestedPayerResponse";
import { Col, Row } from "react-bootstrap";
import moment from "moment";

const PreauthorizationOpticalNested = () => {
  const { preauthorizationRequest, viewPreauthorizationInfo } = useSelector(
    (state) => state.preauthorization
  );

  const { viewClaimInfo, claimRequestData } = useSelector(
    (state) => state.claim
  );

  const visionPreauthorization =
    preauthorizationRequest?.entry?.find(
      (e) => e.resource?.resourceType === "VisionPrescription"
    )?.resource ||
    claimRequestData?.entry?.find(
      (e) => e.resource?.resourceType === "VisionPrescription"
    )?.resource;

  const lensSpecification = visionPreauthorization?.lensSpecification || [];
  const productType =
    visionPreauthorization?.lensSpecification?.[0]?.product?.coding?.[0]
      ?.code || "";

  // Modified function to handle cases without notes
  const getLensSpec = (eye, type) => {
    // First try to find by note
    const byNote = lensSpecification.find(
      (l) =>
        l.eye?.toLowerCase() === eye.toLowerCase() &&
        l.note?.some((n) => n.text?.toLowerCase() === type.toLowerCase())
    );

    if (byNote) return byNote;

    // If no note found, determine by sphere value
    const byValue = lensSpecification.find(
      (l) => l.eye?.toLowerCase() === eye.toLowerCase()
    );

    if (!byValue) return null;

    // Determine type based on sphere value
    const sphere = parseFloat(byValue.sphere) || 0;
    if (type.toLowerCase() === "distance" && sphere >= 0) return byValue;
    if (type.toLowerCase() === "near" && sphere < 0) return byValue;

    return null;
  };

  const getPrism = (lens, base) =>
    lens?.prism?.find((p) => p.base?.toLowerCase() === base?.toLowerCase())
      ?.amount || "";

  const fields =
    productType === "contact"
      ? [
        { label: "POWER", name: "power" },
        { label: "CYLINDER", name: "cylinder" },
        { label: "AXIS", name: "axis" },
        { label: "BACKCURVE", name: "backCurve" },
        { label: "DIAMETER", name: "diameter" },
        { label: "ADD", name: "add" },
        { label: "COLOR", name: "color" },
        { label: "BRAND", name: "brand" },
        { label: "PD", name: "pd" },
      ]
      : [
        { label: "SPHERE", name: "sphere" },
        { label: "CYLINDER", name: "cylinder" },
        { label: "AXIS", name: "axis" },
        { label: "IN", name: "in" },
        { label: "OUT", name: "out" },
        { label: "DOWN", name: "down" },
        { label: "UP", name: "up" },
        { label: "ADD", name: "add" },
        { label: "COLOR", name: "color" },
        { label: "BRAND", name: "brand" },
        { label: "PD", name: "pd" },
      ];

  const leftDistance = getLensSpec("left", "distance");
  const leftNear = getLensSpec("left", "near");
  const rightDistance = getLensSpec("right", "distance");
  const rightNear = getLensSpec("right", "near");

  const opticalData = [
    {
      id: 0,
      title: "Optical Type",
      value: productType || "-",
    },
    {
      id: 1,
      title: "Optical Date",
      value: visionPreauthorization?.dateWritten
        ? moment(visionPreauthorization.dateWritten).format(
          "DD-MM-YYYY, hh:mm:ss"
        )
        : "-",
    },
    {
      id: 2,
      title: "Is Frame",
      value: `${viewPreauthorizationInfo?.other_fields?.vision_extraflds?.isframe ||
        viewClaimInfo?.other_fields?.vision_extraflds?.isframe ||
        "-"
        }`,
    },
    {
      id: 3,
      title: "Regular Contact",
      value:
        viewPreauthorizationInfo?.other_fields?.vision_extraflds
          ?.regular_contact ||
        viewClaimInfo?.other_fields?.vision_extraflds?.regular_contact ||
        "-",
    },
    {
      id: 4,
      title: "Regular Lenses",
      value:
        viewPreauthorizationInfo?.other_fields?.vision_extraflds
          ?.regular_lenses ||
        viewClaimInfo?.other_fields?.vision_extraflds?.regular_lenses ||
        "-",
    },
  ];

  return (
    <div>
      {/* Info Header */}
      <Row>
        {opticalData.map((item) => (
          <Col key={item?.id} xs={12} md={4} xl={3} className="mb-3">
            <Item title={item.title} value={item.value} remark={false} />
          </Col>
        ))}
        <Col xs={12} md={4} xl={3} className="mb-3">
          <div className="d-flex flex-column gap-1 w-100">
            <div className="fs-10 fw-bold">Lens Specifications</div>
            <div className="text-600 fw-normal text-capitalize fs-10">
              {Array.isArray(
                viewPreauthorizationInfo?.other_fields?.vision_extraflds
                  ?.lens_specifications
              ) &&
                viewPreauthorizationInfo?.other_fields?.vision_extraflds
                  ?.lens_specifications?.length > 0
                ? viewPreauthorizationInfo?.other_fields?.vision_extraflds?.lens_specifications.join(
                  " - "
                )
                : "-"}
            </div>
          </div>
        </Col>
      </Row>


      <div className="table-responsive mt-3">
        <table
          className="table align-middle text-center"
          style={{ border: "1px solid #dee2e6", tableLayout: "fixed" }}
        >
          <thead>
            <tr>
              <th
                colSpan={2}
                className="text-success border border-secondary"
                style={{ width: "40%" }}
              >
                Left
              </th>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              ></th>
              <th
                colSpan={2}
                className="text-success border border-secondary"
                style={{ width: "40%" }}
              >
                Right
              </th>
            </tr>
            <tr>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              >
                Distance
              </th>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              >
                Near
              </th>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              ></th>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              >
                Distance
              </th>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              >
                Near
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field.name}>
                {/* LEFT DISTANCE */}
                <td
                  className="border border-secondary"
                  style={{ width: "20%" }}
                >
                  {field.name === "in" ||
                    field.name === "out" ||
                    field.name === "up" ||
                    field.name === "down"
                    ? getPrism(leftDistance, field.name)
                    : leftDistance?.[field.name] || ""}
                </td>
                {/* LEFT NEAR */}
                <td
                  className="border border-secondary"
                  style={{ width: "20%" }}
                >
                  {field.name === "in" ||
                    field.name === "out" ||
                    field.name === "up" ||
                    field.name === "down"
                    ? getPrism(leftNear, field.name)
                    : leftNear?.[field.name] || ""}
                </td>
                {/* LABEL */}
                <td
                  className="text-uppercase text-center text-nowrap border border-secondary"
                  style={{ width: "20%" }}
                >
                  <strong className="fs-10">{field.label}</strong>
                </td>
                {/* RIGHT DISTANCE */}
                <td
                  className="border border-secondary"
                  style={{ width: "20%" }}
                >
                  {field.name === "in" ||
                    field.name === "out" ||
                    field.name === "up" ||
                    field.name === "down"
                    ? getPrism(rightDistance, field.name)
                    : rightDistance?.[field.name] || ""}
                </td>
                {/* RIGHT NEAR */}
                <td
                  className="border border-secondary"
                  style={{ width: "20%" }}
                >
                  {field.name === "in" ||
                    field.name === "out" ||
                    field.name === "up" ||
                    field.name === "down"
                    ? getPrism(rightNear, field.name)
                    : rightNear?.[field.name] || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default PreauthorizationOpticalNested;